﻿import React, {useCallback} from 'react';
import { InventoryReportViewVO } from '../../../types/model/valueObject/InventoryReportViewVO';
import { SupplierCurrentInventoryCardListItem, type SupplierCurrentInventoryCardListItemToggleHandler } from "./SupplierCurrentInventoryCardListItem";
import { withLoader } from "../../hocs/withLoader";
import { SupplierCurrentInventoryCardListLoader } from "./SupplierCurrentInventoryCardListLoader";
import { ModalType } from './data';
import type { Account } from '../../../types/Account';
import { useChunkedItems } from '../../../hooks/render/useChunkedItems';
import InfoCardLoader from "../../deprecatedUikit/InfoCard/InfoCardLoader";

/**
 * Number of cards to render on the first frame.
 * Matches {@link SupplierCurrentInventoryCardListLoader}'s skeleton count so the
 * loader-to-content transition has no visible height shift.
 */
const INITIAL_CHUNK = 10;

/** Number of additional cards to mount per animation frame during progressive rendering. */
const CHUNK_BATCH_SIZE = 10;

type SupplierCurrentInventoryCardListProps = {
  currentInventories?: InventoryReportViewVO[];
  expandByIndex: boolean[];
  onToggleExpand: (index: number) => void;
  setOpenModal: (modal: ModalType) => void;
  setAssetBeingActioned: (asset: InventoryReportViewVO) => void;
  account: Pick<Account, 'userType'>;
}

const SupplierCurrentInventoryCardList = ({
  currentInventories,
  expandByIndex,
  onToggleExpand,
  setOpenModal,
  setAssetBeingActioned,
  account,
}: SupplierCurrentInventoryCardListProps) => {
  // Both hooks must be called before any conditional return (Rules of Hooks).
  const handleToggleExpand = useCallback<SupplierCurrentInventoryCardListItemToggleHandler>((_event, index) => {
    onToggleExpand(index);
  }, [onToggleExpand]);

  const { visibleItems, isChunking } = useChunkedItems({
    items: currentInventories,
    initialCount: INITIAL_CHUNK,
    batchSize: CHUNK_BATCH_SIZE,
  });

  if (!currentInventories?.length) {
    return null;
  }

  // Trailing skeleton placeholders while remaining chunks are pending,
  // capped at CHUNK_BATCH_SIZE so the count never exceeds the next batch.
  const remainingCount = currentInventories.length - visibleItems.length;
  const trailingSkeletonCount = Math.min(CHUNK_BATCH_SIZE, remainingCount);

  return <>
    {visibleItems.map((item, index) =>
      <SupplierCurrentInventoryCardListItem
        key={item.id}
        item={item}
        expanded={expandByIndex[index]}
        index={index}
        onToggleExpand={handleToggleExpand}
        setOpenModal={setOpenModal}
        setAssetBeingActioned={setAssetBeingActioned}
        account={account}
      />
    )}
    {isChunking && Array.from({ length: trailingSkeletonCount }).map((_, i) => (
      <InfoCardLoader key={`chunking-skeleton-${i}`} />
    ))}
  </>
};

const SupplierCurrentInventoryCardListWithLoader = withLoader(SupplierCurrentInventoryCardList, SupplierCurrentInventoryCardListLoader);

export { SupplierCurrentInventoryCardList, SupplierCurrentInventoryCardListWithLoader };
